if (!customElements.get("video-section-slider")) {
    customElements.define(
        "video-section-slider",
        class VideoSectionSlider extends HTMLElement {
            connectedCallback() {
                if (this.initialized) return;

                this.initialized = true;
                this.track = this.querySelector("[data-video-section-track]");
                this.originalSlides = Array.from(
                    this.querySelectorAll("[data-video-section-slide]"),
                );
                this.total = this.originalSlides.length;
                this.current = Math.floor(this.total / 2);
                this.cloneCount = this.total > 1 ? this.total : 0;
                this.currentOffset = 0;
                this.dragStartOffset = 0;
                this.startX = 0;
                this.deltaX = 0;
                this.dragging = false;
                this.suppressClick = false;
                this.pendingLoopReset = null;
                this.autoplay = this.dataset.autoplay === "true";
                this.autoplayDelay = Number(this.dataset.autoplayDelay) || 4000;

                if (!this.track || !this.total) return;

                this.createLoopClones();
                this.slides = Array.from(
                    this.track.querySelectorAll("[data-video-section-slide]"),
                );
                this.currentPhysical = this.cloneCount + this.current;
                this.videoPreviewsPromise = this.prepareVideoPreviews();
                this.captureConfiguredWidths();
                this.setSlideTransitions(false);
                this.updateDynamicWidths();
                this.measureRoleWidths();
                this.setViewportHeight();

                this.onResize = () => {
                    this.setSlideTransitions(false);
                    this.updateDynamicWidths();
                    this.measureRoleWidths();
                    this.setViewportHeight();
                    this.position(false);
                    if (this.classList.contains("is-ready")) {
                        requestAnimationFrame(() =>
                            this.setSlideTransitions(true),
                        );
                    } else {
                        this.scheduleReveal();
                    }
                };
                this.onKeydown = this.handleKeydown.bind(this);
                this.onClick = this.handleClick.bind(this);
                this.onPointerDown = this.handlePointerDown.bind(this);
                this.onPointerMove = this.handlePointerMove.bind(this);
                this.onPointerUp = this.handlePointerEnd.bind(this);
                this.onPointerCancel = (event) =>
                    this.handlePointerEnd(event, true);
                this.onTransitionEnd = this.handleTransitionEnd.bind(this);

                this.setAttribute("tabindex", "0");
                this.addEventListener("keydown", this.onKeydown);
                this.addEventListener("click", this.onClick);
                this.addEventListener("pointerdown", this.onPointerDown);
                this.addEventListener("pointermove", this.onPointerMove);
                this.addEventListener("pointerup", this.onPointerUp);
                this.addEventListener("pointercancel", this.onPointerCancel);
                this.track.addEventListener(
                    "transitionend",
                    this.onTransitionEnd,
                );
                window.addEventListener("resize", this.onResize);

                requestAnimationFrame(() => {
                    this.position(false);
                    this.scheduleReveal();
                });
                this.scheduleAutoplay();
            }

            disconnectedCallback() {
                window.clearTimeout(this.autoplayTimer);
                window.clearTimeout(this.revealTimer);
                window.clearTimeout(this.playRequestTimer);
                window.removeEventListener("resize", this.onResize);
                this.removeEventListener("keydown", this.onKeydown);
                this.removeEventListener("click", this.onClick);
                this.removeEventListener("pointerdown", this.onPointerDown);
                this.removeEventListener("pointermove", this.onPointerMove);
                this.removeEventListener("pointerup", this.onPointerUp);
                this.removeEventListener("pointercancel", this.onPointerCancel);
                if (this.track) {
                    this.track.removeEventListener(
                        "transitionend",
                        this.onTransitionEnd,
                    );
                }
            }

            scheduleReveal() {
                window.clearTimeout(this.revealTimer);
                this.revealTimer = window.setTimeout(() => {
                    if (!this.isConnected) return;

                    this.setSlideTransitions(false);
                    this.updateDynamicWidths();
                    this.measureRoleWidths();
                    this.setViewportHeight();
                    this.position(false);

                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (!this.isConnected) return;
                            this.track.style.removeProperty(
                                "transition-duration",
                            );
                            this.setSlideTransitions(true);
                            this.classList.add("is-ready");
                            this.setAttribute("aria-busy", "false");
                        });
                    });
                }, 300);
            }

            createLoopClones() {
                this.originalSlides.forEach((slide, index) => {
                    slide.dataset.logicalIndex = String(index);
                });

                if (!this.cloneCount) return;

                const before = document.createDocumentFragment();
                const after = document.createDocumentFragment();

                for (
                    let index = this.total - this.cloneCount;
                    index < this.total;
                    index += 1
                ) {
                    before.appendChild(
                        this.cloneSlide(this.originalSlides[index], index),
                    );
                }

                for (let index = 0; index < this.cloneCount; index += 1) {
                    after.appendChild(
                        this.cloneSlide(this.originalSlides[index], index),
                    );
                }

                this.track.prepend(before);
                this.track.append(after);
            }

            cloneSlide(source, logicalIndex) {
                const clone = source.cloneNode(true);
                clone.dataset.logicalIndex = String(logicalIndex);
                clone.dataset.loopClone = "true";
                clone.classList.remove("is-active", "is-adjacent");
                clone.setAttribute("aria-hidden", "true");
                clone.removeAttribute("id");
                clone.removeAttribute("data-shopify-editor-block");
                clone.querySelectorAll("[id]").forEach((element) => {
                    element.removeAttribute("id");
                });
                clone
                    .querySelectorAll("button, a, input, select, textarea")
                    .forEach((element) => {
                        element.setAttribute("tabindex", "-1");
                    });
                clone.querySelectorAll("video").forEach((video) => {
                    video.pause();
                    video.preload = "none";
                });
                return clone;
            }

            prepareVideoPreviews() {
                const visiblePreviews = [];

                this.slides.forEach((slide, physicalIndex) => {
                    const video = slide.querySelector("video");
                    const media = slide.querySelector(
                        "[data-video-section-media]",
                    );
                    if (!video || !media) return;

                    video.removeAttribute("poster");
                    video.preload = "auto";

                    const previewReady = new Promise((resolve) => {
                        const showVideoFrame = () => {
                            media.classList.add("is-video-preview-ready");
                            resolve();
                        };

                        if (video.readyState >= 2) {
                            showVideoFrame();
                            return;
                        }

                        video.addEventListener("loadeddata", showVideoFrame, {
                            once: true,
                        });
                        video.addEventListener("error", resolve, { once: true });
                    });

                    if (Math.abs(physicalIndex - this.currentPhysical) <= 2) {
                        visiblePreviews.push(previewReady);
                    }
                    try {
                        video.load();
                    } catch {
                        // Keep the poster visible when the preview frame cannot load.
                    }
                });

                return Promise.race([
                    Promise.all(visiblePreviews),
                    new Promise((resolve) => window.setTimeout(resolve, 1000)),
                ]);
            }

            normalizeIndex(index) {
                return ((index % this.total) + this.total) % this.total;
            }

            captureConfiguredWidths() {
                const styles = getComputedStyle(this);
                this.configuredRoleWidths = {
                    small:
                        parseFloat(
                            styles.getPropertyValue("--vs-slide-small-width"),
                        ) || 234,
                    adjacent:
                        parseFloat(
                            styles.getPropertyValue(
                                "--vs-slide-adjacent-width",
                            ),
                        ) || 293,
                    active:
                        parseFloat(
                            styles.getPropertyValue("--vs-slide-active-width"),
                        ) || 366,
                };
            }

            setSlideTransitions(enabled) {
                this.slides.forEach((slide) => {
                    if (enabled) {
                        slide.style.removeProperty("transition-duration");
                    } else {
                        slide.style.transitionDuration = "0ms";
                    }
                });
            }

            updateDynamicWidths() {
                if (!this.configuredRoleWidths) return;

                if (window.matchMedia("(max-width: 767px)").matches) {
                    this.style.removeProperty("--vs-slide-small-width");
                    this.style.removeProperty("--vs-slide-adjacent-width");
                    this.style.removeProperty("--vs-slide-active-width");
                    return;
                }

                const styles = getComputedStyle(this);
                const gap =
                    parseFloat(getComputedStyle(this.track).columnGap) || 0;
                const edgeCrop =
                    parseFloat(styles.getPropertyValue("--vs-edge-crop")) || 0;
                const configuredCardsWidth =
                    this.configuredRoleWidths.active +
                    this.configuredRoleWidths.adjacent * 2 +
                    this.configuredRoleWidths.small * 2;
                const availableCardsWidth = Math.max(
                    this.clientWidth + edgeCrop * 2 - gap * 4,
                    1,
                );
                const scale = availableCardsWidth / configuredCardsWidth;

                this.style.setProperty(
                    "--vs-slide-small-width",
                    `${this.configuredRoleWidths.small * scale}px`,
                );
                this.style.setProperty(
                    "--vs-slide-adjacent-width",
                    `${this.configuredRoleWidths.adjacent * scale}px`,
                );
                this.style.setProperty(
                    "--vs-slide-active-width",
                    `${this.configuredRoleWidths.active * scale}px`,
                );
            }

            measureRoleWidths() {
                const active = this.slides[this.currentPhysical];
                const adjacent = this.slides[this.currentPhysical - 1];
                const small = this.slides[this.currentPhysical - 2];
                if (!active) return;

                this.roleWidths = {
                    active: active.offsetWidth,
                    adjacent: adjacent
                        ? adjacent.offsetWidth
                        : active.offsetWidth,
                    small: small ? small.offsetWidth : active.offsetWidth,
                };
            }

            setViewportHeight() {
                const tallestSlide = this.slides.reduce(
                    (maxHeight, slide) =>
                        Math.max(maxHeight, slide.offsetHeight),
                    0,
                );

                if (tallestSlide > 0) {
                    this.style.setProperty(
                        "--vs-viewport-height",
                        `${tallestSlide}px`,
                    );
                }
            }

            roleWidthForDistance(distance) {
                if (distance === 0) return this.roleWidths.active;
                if (distance === 1) return this.roleWidths.adjacent;
                return this.roleWidths.small;
            }

            getFinalOffset(physicalIndex) {
                if (!this.roleWidths) return 0;

                const gap =
                    parseFloat(getComputedStyle(this.track).columnGap) || 0;
                let left = 0;

                for (let index = 0; index < physicalIndex; index += 1) {
                    left +=
                        this.roleWidthForDistance(
                            Math.abs(index - physicalIndex),
                        ) + gap;
                }

                return this.clientWidth / 2 - left - this.roleWidths.active / 2;
            }

            getActualCenteredOffset(physicalIndex) {
                const slide = this.slides[physicalIndex];
                if (!slide) return this.currentOffset;
                return (
                    this.clientWidth / 2 -
                    slide.offsetLeft -
                    slide.offsetWidth / 2
                );
            }

            interpolateDragWidths(targetPhysical, progress) {
                this.hasDragWidths = true;
                this.slides.forEach((slide, physicalIndex) => {
                    const startWidth = this.dragStartWidths[physicalIndex];
                    const endWidth = this.roleWidthForDistance(
                        Math.abs(physicalIndex - targetPhysical),
                    );
                    const width =
                        startWidth + (endWidth - startWidth) * progress;
                    slide.style.width = `${width}px`;
                });
            }

            goTo(logicalIndex) {
                const normalized = this.normalizeIndex(logicalIndex);
                let nearestPhysical = this.currentPhysical;
                let nearestDistance = Infinity;

                this.slides.forEach((slide, physicalIndex) => {
                    if (Number(slide.dataset.logicalIndex) !== normalized)
                        return;
                    const distance = Math.abs(
                        physicalIndex - this.currentPhysical,
                    );
                    if (distance < nearestDistance) {
                        nearestDistance = distance;
                        nearestPhysical = physicalIndex;
                    }
                });

                this.goToPhysical(nearestPhysical);
            }

            goToPhysical(physicalIndex) {
                const slide = this.slides[physicalIndex];
                if (!slide) {
                    this.position(true);
                    return;
                }

                this.currentPhysical = physicalIndex;
                this.current = Number(slide.dataset.logicalIndex);
                this.pendingLoopReset = null;

                const originalStart = this.cloneCount;
                const originalEnd = this.cloneCount + this.total - 1;
                if (
                    physicalIndex < originalStart ||
                    physicalIndex > originalEnd
                ) {
                    this.pendingLoopReset = this.cloneCount + this.current;
                }

                this.pauseOtherVideos(physicalIndex);
                this.position(true);
                this.scheduleAutoplay();
            }

            scheduleAutoplay() {
                window.clearTimeout(this.autoplayTimer);
                if (!this.autoplay || this.total < 2) return;

                this.autoplayTimer = window.setTimeout(() => {
                    if (
                        this.querySelector(
                            "[data-video-section-media].is-playing",
                        )
                    ) {
                        this.scheduleAutoplay();
                        return;
                    }
                    this.goTo(this.current + 1);
                }, this.autoplayDelay);
            }

            position(animate = true) {
                const activeSlide = this.slides[this.currentPhysical];
                if (!activeSlide) return;

                this.track.style.transitionDuration = animate ? "" : "0ms";
                this.slides.forEach((slide, physicalIndex) => {
                    const isActive = physicalIndex === this.currentPhysical;
                    slide.classList.toggle("is-active", isActive);
                    slide.classList.toggle(
                        "is-adjacent",
                        Math.abs(physicalIndex - this.currentPhysical) === 1,
                    );
                    slide.setAttribute(
                        "aria-current",
                        isActive ? "true" : "false",
                    );
                });

                if (this.hasDragWidths) {
                    this.track.offsetHeight;
                    this.slides.forEach((slide) => {
                        slide.style.removeProperty("transition-duration");
                        slide.style.removeProperty("width");
                    });
                    this.hasDragWidths = false;
                }

                requestAnimationFrame(() => {
                    const offset = this.getFinalOffset(this.currentPhysical);
                    this.currentOffset = offset;
                    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
                });
            }

            handleTransitionEnd(event) {
                if (event.propertyName !== "transform") return;

                if (this.pendingLoopReset !== null) {
                    this.currentPhysical = this.pendingLoopReset;
                    this.pendingLoopReset = null;
                    this.position(false);
                    requestAnimationFrame(() => this.playRequestedVideo());
                    return;
                }

                this.playRequestedVideo();
            }

            handleKeydown(event) {
                if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    this.goTo(this.current - 1);
                }
                if (event.key === "ArrowRight") {
                    event.preventDefault();
                    this.goTo(this.current + 1);
                }
            }

            handleClick(event) {
                const slide = event.target.closest("[data-video-section-slide]");
                if (!slide || !this.contains(slide)) return;

                if (this.suppressClick) {
                    event.preventDefault();
                    return;
                }

                const physicalIndex = this.slides.indexOf(slide);
                const playButton = event.target.closest(
                    "[data-video-section-play]",
                );

                if (playButton) {
                    event.preventDefault();
                    event.stopPropagation();
                    if (physicalIndex !== this.currentPhysical) {
                        this.playAfterTransition = true;
                        window.clearTimeout(this.playRequestTimer);
                        this.playRequestTimer = window.setTimeout(
                            () => this.playRequestedVideo(),
                            650,
                        );
                        this.goToPhysical(physicalIndex);
                        return;
                    }
                    this.toggleVideo(slide, playButton);
                    return;
                }

                const media = slide.querySelector("[data-video-section-media]");
                if (
                    physicalIndex === this.currentPhysical &&
                    media?.classList.contains("is-playing")
                ) {
                    event.preventDefault();
                    const button = slide.querySelector(
                        "[data-video-section-play]",
                    );
                    if (button) this.toggleVideo(slide, button);
                    return;
                }

                this.playAfterTransition = false;
                window.clearTimeout(this.playRequestTimer);
                this.goToPhysical(physicalIndex);
            }

            playRequestedVideo() {
                if (!this.playAfterTransition) return;

                this.playAfterTransition = false;
                window.clearTimeout(this.playRequestTimer);
                const slide = this.slides[this.currentPhysical];
                const button = slide?.querySelector("[data-video-section-play]");
                if (slide && button) this.toggleVideo(slide, button);
            }

            handlePointerDown(event) {
                if (event.pointerType === "mouse" && event.button !== 0) return;

                window.clearTimeout(this.autoplayTimer);
                window.clearTimeout(this.playRequestTimer);
                this.playAfterTransition = false;
                this.dragging = true;
                this.pointerCaptured = false;
                this.suppressClick = false;
                this.pointerId = event.pointerId;
                this.startX = event.clientX;
                this.dragStartedAt = performance.now();
                this.deltaX = 0;
                this.dragStartOffset = this.currentOffset;
                this.dragStartWidths = this.slides.map(
                    (slide) => slide.offsetWidth,
                );
                this.track.style.transitionDuration = "0ms";
                this.slides.forEach((slide) => {
                    slide.style.transitionDuration = "0ms";
                });
                this.classList.add("is-dragging");
            }

            handlePointerMove(event) {
                if (!this.dragging || event.pointerId !== this.pointerId)
                    return;

                this.deltaX = event.clientX - this.startX;
                if (Math.abs(this.deltaX) > 4) {
                    this.suppressClick = true;
                    if (!this.pointerCaptured) {
                        this.setPointerCapture(event.pointerId);
                        this.pointerCaptured = true;
                    }
                }

                const direction = this.deltaX < 0 ? 1 : -1;
                const targetPhysical = this.currentPhysical + direction;
                const progress = Math.min(
                    Math.abs(this.deltaX) /
                        Math.max(this.roleWidths.active * 0.7, 120),
                    1,
                );

                this.interpolateDragWidths(targetPhysical, progress);
                const centeredOffset = this.getActualCenteredOffset(
                    this.currentPhysical,
                );
                this.track.style.transform = `translate3d(${centeredOffset + this.deltaX}px, 0, 0)`;
            }

            handlePointerEnd(event, cancelled = false) {
                if (!this.dragging || event.pointerId !== this.pointerId)
                    return;

                this.dragging = false;
                this.classList.remove("is-dragging");
                if (
                    this.pointerCaptured &&
                    this.hasPointerCapture(event.pointerId)
                ) {
                    this.releasePointerCapture(event.pointerId);
                }
                this.pointerCaptured = false;
                this.slides.forEach((slide) => {
                    slide.style.removeProperty("transition-duration");
                });

                const dragDuration = Math.max(
                    performance.now() - this.dragStartedAt,
                    1,
                );
                const dragVelocity = Math.abs(this.deltaX) / dragDuration;
                const shouldChange =
                    !cancelled &&
                    (Math.abs(this.deltaX) >= 45 ||
                        (Math.abs(this.deltaX) >= 12 && dragVelocity > 0.25));

                if (shouldChange) {
                    this.goTo(this.current + (this.deltaX < 0 ? 1 : -1));
                } else {
                    this.position(true);
                    this.scheduleAutoplay();
                }

                if (this.suppressClick) {
                    window.setTimeout(() => {
                        this.suppressClick = false;
                    }, 0);
                }
            }

            pauseOtherVideos(activePhysicalIndex) {
                this.slides.forEach((slide, physicalIndex) => {
                    if (physicalIndex === activePhysicalIndex) return;
                    const video = slide.querySelector("video");
                    const media = slide.querySelector(
                        "[data-video-section-media]",
                    );
                    const button = slide.querySelector(
                        "[data-video-section-play]",
                    );
                    if (video) video.pause();
                    if (media) media.classList.remove("is-playing");
                    if (media) media.classList.remove("is-started");
                    if (button)
                        button.setAttribute(
                            "aria-label",
                            button.dataset.playLabel,
                        );
                });
            }

            toggleVideo(slide, button) {
                const video = slide.querySelector("video");
                const media = slide.querySelector("[data-video-section-media]");
                if (!video || !media) return;

                if (video.paused) {
                    this.pauseOtherVideos(this.currentPhysical);
                    const promise = video.play();
                    if (promise) {
                        promise
                            .then(() => {
                                media.classList.add("is-started", "is-playing");
                                button.setAttribute(
                                    "aria-label",
                                    button.dataset.pauseLabel,
                                );
                            })
                            .catch(() => {});
                    }
                } else {
                    video.pause();
                    media.classList.remove("is-playing");
                    button.setAttribute("aria-label", button.dataset.playLabel);
                }
            }
        },
    );
}
